import { InteractionEvent } from 'pixi.js';

import { CanvasDebugger } from '../../../debug/debugger';

export const CANVAS_ZOOM = 'canvas-zoom';
export const CANVAS_CLICK = 'canvas-click';

type EventType = typeof CANVAS_ZOOM | typeof CANVAS_CLICK;

export type CanvasEventArgs = {
  [CANVAS_ZOOM]: { zoom: number };
  [CANVAS_CLICK]: InteractionEvent;
};

export type CanvasEventCallback<TEvent extends EventType = EventType> = (
  args: CanvasEventArgs[TEvent],
) => void;

export class CanvasEvents {
  private debugger: CanvasDebugger | null = null;
  private listeners: Record<EventType, CanvasEventCallback[]> = {} as Record<
    EventType,
    CanvasEventCallback[]
  >;

  public registerDebugger(canvasDebugger: CanvasDebugger): void {
    this.debugger = canvasDebugger;
  }

  public subscribe<TEvent extends EventType>(
    type: TEvent,
    callback: CanvasEventCallback<TEvent>,
  ): void {
    const listeners = this.listeners;

    if (!listeners[type]) {
      listeners[type] = [];
    }

    listeners[type].push(callback as CanvasEventCallback<EventType>);
  }

  public unsubscribe<TEvent extends EventType = EventType>(
    callback: CanvasEventCallback<TEvent>,
  ): void {
    const listeners = this.listeners;

    Object.values(listeners).forEach((callbacks) => {
      const index = callbacks.indexOf(
        callback as CanvasEventCallback<EventType>,
      );

      if (index > -1) {
        callbacks.splice(index, 1);
      }
    });
  }

  public publish<TEvent extends keyof CanvasEventArgs>(
    type: TEvent,
    args: CanvasEventArgs[TEvent],
  ): void {
    const canvasDebugger = this.debugger;
    const listeners = this.listeners[type];

    canvasDebugger?.debug('Events', `Event ${type}`, args);

    if (!listeners) {
      return;
    }

    listeners.forEach((callback) => callback(args));
  }
}

export default new CanvasEvents();
